import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { MarkdownRendererProps } from '../../types/message';

/**
 * Markdown レンダラーコンポーネント
 * 基本的なMarkdown構文をサポート（太字、斜体、コード、コードブロック）
 * XSS対策のため、HTML要素は制限的にサポート
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
}) => {
  const theme = useTheme();

  // HTML エスケープ関数（XSS対策）
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Markdown を HTML に変換する関数
  const parseMarkdown = (text: string): string => {
    let html = escapeHtml(text);

    // コードブロック（```言語）
    html = html.replace(
      /```(\w+)?\n([\s\S]*?)```/g,
      (_, language, code) => {
        const lang = language || 'text';
        return `<pre class="code-block" data-language="${lang}"><code>${code.trim()}</code></pre>`;
      }
    );

    // インラインコード（`code`）
    html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

    // 見出し（# H1, ## H2, ### H3, #### H4）
    html = html.replace(/^#### (.*$)/gm, '<h4 class="heading-4">$1</h4>');
    html = html.replace(/^### (.*$)/gm, '<h3 class="heading-3">$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2 class="heading-2">$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1 class="heading-1">$1</h1>');

    // Markdownリンク記法（[テキスト](URL)）
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="markdown-link">$1</a>'
    );

    // URL自動リンク化（http(s)://から始まるURL）- Markdownリンクでない単体のURL
    html = html.replace(
      /(^|[^"'>])(https?:\/\/[^\s<>"]{2,})(?![^<]*<\/a>)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="auto-link">$2</a>'
    );

    // 箇条書き（- item または * item）
    html = html.replace(/^[\-\*] (.+)$/gm, '<li class="list-item">$1</li>');
    // 連続するリストアイテムをul要素でラップ（改行文字を考慮）
    html = html.replace(/(<li class="list-item">.*?<\/li>(?:\n<li class="list-item">.*?<\/li>)*)/g, '<ul class="list">$1</ul>');
    html = html.replace(/<\/li>\n<li class="list-item">/g, '</li><li class="list-item">');

    // 番号付きリスト（1. item）
    html = html.replace(/^\d+\. (.+)$/gm, '<li class="list-item-ordered">$1</li>');
    // 連続するリストアイテムをol要素でラップ（改行文字を考慮）
    html = html.replace(/(<li class="list-item-ordered">.*?<\/li>(?:\n<li class="list-item-ordered">.*?<\/li>)*)/g, '<ol class="list-ordered">$1</ol>');
    html = html.replace(/<\/li>\n<li class="list-item-ordered">/g, '</li><li class="list-item-ordered">');

    // 太字（**text** または __text__）
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // 斜体（*text* または _text_）- 箇条書きと重複しないように後で処理
    html = html.replace(/\*([^\*\n]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');

    // 改行処理
    html = html.replace(/\n/g, '<br>');

    return html;
  };

  const htmlContent = parseMarkdown(content);

  return (
    <Box
      className={className}
      sx={{
        '& .code-block': {
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1.5),
          margin: theme.spacing(1, 0),
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '0.875rem',
          lineHeight: 1.4,
          overflow: 'auto',
          position: 'relative',
          '&::before': {
            content: 'attr(data-language)',
            position: 'absolute',
            top: theme.spacing(0.5),
            right: theme.spacing(1),
            fontSize: '0.75rem',
            color: theme.palette.grey[600],
            textTransform: 'uppercase',
          },
        },
        '& .inline-code': {
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.spacing(0.5),
          padding: theme.spacing(0.25, 0.5),
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '0.875em',
        },
        // 見出しスタイル
        '& .heading-1': {
          fontSize: '1.5rem',
          fontWeight: theme.typography.fontWeightBold,
          margin: theme.spacing(2, 0, 1, 0),
          color: theme.palette.text.primary,
        },
        '& .heading-2': {
          fontSize: '1.3rem',
          fontWeight: theme.typography.fontWeightBold,
          margin: theme.spacing(1.5, 0, 1, 0),
          color: theme.palette.text.primary,
        },
        '& .heading-3': {
          fontSize: '1.1rem',
          fontWeight: theme.typography.fontWeightBold,
          margin: theme.spacing(1, 0, 0.5, 0),
          color: theme.palette.text.primary,
        },
        '& .heading-4': {
          fontSize: '1rem',
          fontWeight: theme.typography.fontWeightMedium,
          margin: theme.spacing(1, 0, 0.5, 0),
          color: theme.palette.text.primary,
        },
        // リストスタイル
        '& .list': {
          margin: theme.spacing(1, 0),
          paddingLeft: theme.spacing(2),
          '& .list-item': {
            marginBottom: theme.spacing(0.5),
            lineHeight: 1.5,
          },
        },
        '& .list-ordered': {
          margin: theme.spacing(1, 0),
          paddingLeft: theme.spacing(2),
          '& .list-item-ordered': {
            marginBottom: theme.spacing(0.5),
            lineHeight: 1.5,
          },
        },
        '& strong': {
          fontWeight: theme.typography.fontWeightBold,
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& br': {
          lineHeight: 1.6,
        },
        // 自動リンクスタイル
        '& .auto-link': {
          color: theme.palette.primary.light,
          '&:hover': {
            color: theme.palette.primary.main,
          },
        },
        // Markdownリンクスタイル
        '& .markdown-link': {
          color: theme.palette.primary.light,
          '&:hover': {
            color: theme.palette.primary.main,
          },
        },
        // ダークモード対応
        ...(theme.palette.mode === 'dark' && {
          '& .code-block': {
            backgroundColor: theme.palette.grey[800],
            border: `1px solid ${theme.palette.grey[600]}`,
            '&::before': {
              color: theme.palette.grey[400],
            },
          },
          '& .inline-code': {
            backgroundColor: theme.palette.grey[800],
            border: `1px solid ${theme.palette.grey[600]}`,
          },
          '& .heading-1, & .heading-2, & .heading-3, & .heading-4': {
            color: theme.palette.text.primary,
          },
          '& .auto-link': {
            color: theme.palette.primary.light,
            '&:hover': {
              color: theme.palette.primary.main,
            },
          },
          '& .markdown-link': {
            color: theme.palette.primary.light,
            '&:hover': {
              color: theme.palette.primary.main,
            },
          },
        }),
      }}
    >
      <Typography
        component="div"
        variant="body1"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      />
    </Box>
  );
};